/**
 * AssignDoorDialog — prompts the scheduler to pick a dock door, used when
 * marking a door-less appointment as arrived. Every ARRIVED appointment must
 * have a door (the receiving KPI and the dock-truck kiosk both depend on it),
 * so when a queued appointment is marked arrived, this dialog captures the door
 * the warehouse assigns (the "walkie-talkie" answer) and arrival proceeds with it.
 *
 * Reuses the app's Radix dialog + select primitives. Doors come from the
 * already-loaded calendar.doors list (no extra fetch).
 */
import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';

export interface DoorOption {
    dockDoorId: number;
    code: string;
    name?: string | null;
}

interface AssignDoorDialogProps {
    /** Open state controlled by the parent. */
    open: boolean;
    /** The appointment label shown in the dialog (e.g. "A00000019" or carrier). */
    appointmentLabel: string;
    /** Doors available at this facility (from calendar.doors). */
    doors: DoorOption[];
    /** Called with the chosen door id when the scheduler confirms. */
    onConfirm: (dockDoorId: number) => void;
    /** Called when the dialog is dismissed without choosing. */
    onCancel: () => void;
    /** True while the arrive call is in flight (disables the button). */
    busy?: boolean;
}

export function AssignDoorDialog({
    open,
    appointmentLabel,
    doors,
    onConfirm,
    onCancel,
    busy,
}: AssignDoorDialogProps) {
    const [selectedId, setSelectedId] = useState<string>('');

    const handleConfirm = () => {
        const id = parseInt(selectedId, 10);
        if (Number.isInteger(id) && id > 0) onConfirm(id);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Assign a dock door</DialogTitle>
                    <DialogDescription>
                        {appointmentLabel} has no door assigned. Choose the door the truck is
                        pulling into — arrival will be recorded against it.
                    </DialogDescription>
                </DialogHeader>

                <div className="py-2">
                    <Select value={selectedId} onValueChange={setSelectedId}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select a dock door" />
                        </SelectTrigger>
                        <SelectContent>
                            {doors.map((d) => (
                                <SelectItem key={d.dockDoorId} value={String(d.dockDoorId)}>
                                    {d.code}{d.name ? ` · ${d.name}` : ''}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={onCancel} disabled={busy}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} disabled={!selectedId || busy}>
                        {busy ? 'Marking arrived…' : 'Assign & mark arrived'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
